// SGNL-34 — Admin Users sub-tab.
//
// Lets the platform admin grant/revoke Signal access and toggle viewer/admin
// roles. Mounted as a sub-tab inside the existing Admin tab (alongside
// Pull & Reclassify and AI Audit).

import { esc, MS_PER_DAY } from "../lib/format";
import type { AccessRequest, SignalUser } from "../types";

// SGNL-34 RBAC-13: render an inline error banner instead of `alert()`.
// Matches the pattern in other views (host.innerHTML error div on load
// failure). Keeps the admin surface consistent with the rest of the app.
function showError(host: HTMLElement, message: string): void {
  let banner = host.querySelector<HTMLDivElement>('[data-testid="admin-error-banner"]');
  if (!banner) {
    banner = document.createElement("div");
    banner.dataset.testid = "admin-error-banner";
    banner.setAttribute("role", "alert");
    banner.className = "error";
    banner.style.cssText =
      "margin:8px 0;padding:8px 12px;background:#FFE2E5;color:#9F1230;border:1px solid #FFC8CE;border-radius:6px;font-size:13px;";
    host.insertBefore(banner, host.firstChild);
  }
  banner.textContent = message;
  // Auto-dismiss after 5s so the banner doesn't permanently occupy
  // space after a transient failure.
  setTimeout(() => banner?.remove(), 5000);
}

function fmtRelative(iso: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < MS_PER_DAY) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / MS_PER_DAY)}d ago`;
}

async function listUsers(): Promise<SignalUser[]> {
  const r = await fetch("/api/admin/users", { credentials: "same-origin" });
  if (!r.ok) throw new Error(`/api/admin/users returned ${r.status}`);
  return (await r.json()) as SignalUser[];
}

async function listRequests(): Promise<AccessRequest[]> {
  const r = await fetch("/api/admin/access-requests", { credentials: "same-origin" });
  if (!r.ok) throw new Error(`/api/admin/access-requests returned ${r.status}`);
  return (await r.json()) as AccessRequest[];
}

async function addUser(email: string, role: "viewer" | "admin"): Promise<void> {
  const r = await fetch("/api/admin/users", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ email, role }),
  });
  if (!r.ok) {
    const body = await r.text();
    throw new Error(`Add user failed (${r.status}): ${body}`);
  }
}

async function setRole(email: string, role: "viewer" | "admin"): Promise<void> {
  const r = await fetch(`/api/admin/users/${encodeURIComponent(email)}/role`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ role }),
  });
  if (!r.ok) {
    const body = await r.text();
    throw new Error(`Role change failed (${r.status}): ${body}`);
  }
}

async function revoke(email: string): Promise<void> {
  const r = await fetch(`/api/admin/users/${encodeURIComponent(email)}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!r.ok) {
    const body = await r.text();
    throw new Error(`Revoke failed (${r.status}): ${body}`);
  }
}

async function reviewRequest(id: number, action: "approve" | "deny"): Promise<void> {
  const r = await fetch(`/api/admin/access-requests/${id}/${action}`, {
    method: "POST",
    credentials: "same-origin",
  });
  if (!r.ok) {
    const body = await r.text();
    throw new Error(`${action} failed (${r.status}): ${body}`);
  }
}

// SGNL-182: list distinct BUs for the Set Scope dropdown.
async function listBus(): Promise<string[]> {
  const r = await fetch("/api/admin/bus", { credentials: "same-origin" });
  if (!r.ok) throw new Error(`/api/admin/bus returned ${r.status}`);
  return (await r.json()) as string[];
}

// SGNL-182: set the user's BU scope. null = global; list = restricted.
async function setScope(email: string, scope: string[] | null): Promise<void> {
  const r = await fetch(`/api/admin/users/${encodeURIComponent(email)}/scope`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ bu_scope: scope }),
  });
  if (!r.ok) {
    const body = await r.text();
    throw new Error(`Set scope failed (${r.status}): ${body}`);
  }
}

// SGNL-182: render the user's current scope as a chip / inline list.
function renderScopeCell(scope: string[] | null): string {
  if (scope === null || scope === undefined) {
    return `<span class="chip" style="background:#E6E0FF;color:#33139F;">Global</span>`;
  }
  if (scope.length === 0) {
    return `<span class="chip" style="background:#FFE2E5;color:#9F1230;">No BUs</span>`;
  }
  const chips = scope
    .map((b) => `<span class="chip" style="margin-right:4px;">${esc(b)}</span>`)
    .join("");
  return chips;
}

// SGNL-182: Set Scope modal. Lightweight inline overlay; no library.
async function openScopeModal(
  host: HTMLElement,
  user: SignalUser,
  onSaved: () => Promise<void>,
): Promise<void> {
  let bus: string[];
  try {
    bus = await listBus();
  } catch (e) {
    showError(host, (e as Error).message);
    return;
  }

  const isGlobal = user.bu_scope === null || user.bu_scope === undefined;
  const allowedSet = new Set(user.bu_scope ?? []);

  const overlay = document.createElement("div");
  overlay.dataset.testid = "scope-modal-backdrop";
  overlay.style.cssText =
    "position:fixed;inset:0;background:rgba(11,17,28,0.55);display:flex;align-items:center;justify-content:center;z-index:9999;";

  const modal = document.createElement("div");
  modal.dataset.testid = "scope-modal";
  modal.style.cssText =
    "background:#fff;border-radius:12px;padding:20px 24px;max-width:480px;width:92%;max-height:80vh;overflow-y:auto;box-shadow:0 24px 48px rgba(0,0,0,0.2);";

  const buCheckboxes = bus
    .map(
      (b) => `
        <label style="display:flex;align-items:center;gap:8px;padding:4px 0;font-size:13px;">
          <input type="checkbox" class="scope-bu-cb" value="${esc(b)}"
                 ${allowedSet.has(b) ? "checked" : ""} />
          ${esc(b)}
        </label>`,
    )
    .join("");

  modal.innerHTML = `
    <h3 style="margin:0 0 4px;font-family:'Space Grotesk',sans-serif;font-size:18px;">
      Set BU scope
    </h3>
    <p class="muted" style="margin:0 0 14px;font-size:12px;">
      ${esc(user.display_name ?? user.email)} — ${esc(user.email)}
    </p>

    <label style="display:flex;align-items:center;gap:8px;font-weight:500;font-size:13px;">
      <input type="radio" name="scope-mode" value="global"
             data-testid="scope-mode-global" ${isGlobal ? "checked" : ""} />
      Global — sees every BU's drill-downs
    </label>
    <label style="display:flex;align-items:center;gap:8px;font-weight:500;font-size:13px;margin-top:6px;">
      <input type="radio" name="scope-mode" value="scoped"
             data-testid="scope-mode-scoped" ${!isGlobal ? "checked" : ""} />
      Specific BUs only
    </label>

    <div data-testid="scope-bu-list"
         style="margin-top:10px;padding-left:24px;${isGlobal ? "opacity:0.4;pointer-events:none;" : ""}">
      ${buCheckboxes || '<div class="muted" style="font-size:12px;">No BUs found in opportunities table.</div>'}
    </div>

    <div style="margin-top:18px;display:flex;justify-content:flex-end;gap:8px;">
      <button class="action-link" data-action="scope-cancel"
              style="padding:6px 14px;border:1px solid var(--line);border-radius:6px;">Cancel</button>
      <button class="action-link" data-action="scope-save" data-testid="scope-save-btn"
              style="padding:6px 14px;border:1px solid var(--purple-rain);border-radius:6px;color:#fff;background:var(--purple-rain);">Save</button>
    </div>`;

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  const dismiss = (): void => overlay.remove();

  // Toggle BU list dim/active based on mode.
  modal.querySelectorAll<HTMLInputElement>('input[name="scope-mode"]').forEach((radio) => {
    radio.addEventListener("change", () => {
      const list = modal.querySelector<HTMLElement>('[data-testid="scope-bu-list"]');
      if (!list) return;
      const scoped = (modal.querySelector<HTMLInputElement>('[data-testid="scope-mode-scoped"]')?.checked) ?? false;
      list.style.opacity = scoped ? "1" : "0.4";
      list.style.pointerEvents = scoped ? "auto" : "none";
    });
  });

  modal.querySelector('[data-action="scope-cancel"]')?.addEventListener("click", dismiss);
  overlay.addEventListener("click", (ev) => {
    // Dismiss on backdrop click (but not on modal click).
    if (ev.target === overlay) dismiss();
  });

  modal.querySelector('[data-action="scope-save"]')?.addEventListener("click", async () => {
    const scoped = (modal.querySelector<HTMLInputElement>('[data-testid="scope-mode-scoped"]')?.checked) ?? false;
    const newScope: string[] | null = scoped
      ? Array.from(modal.querySelectorAll<HTMLInputElement>(".scope-bu-cb"))
          .filter((c) => c.checked)
          .map((c) => c.value)
      : null;
    try {
      await setScope(user.email, newScope);
      dismiss();
      await onSaved();
    } catch (e) {
      showError(host, (e as Error).message);
    }
  });
}

function renderUserRow(u: SignalUser, currentEmail: string): string {
  const isSelf = u.email.toLowerCase() === currentEmail.toLowerCase();
  const roleChip =
    u.role === "admin"
      ? `<span class="chip indigo" data-testid="user-role-pill">Admin</span>`
      : `<span class="chip" data-testid="user-role-pill">Viewer</span>`;
  const toggleDisabled = isSelf ? "disabled" : "";
  const removeDisabled = isSelf ? "disabled" : "";
  const toggleLabel = u.role === "admin" ? "→ Viewer" : "→ Admin";
  return `
    <tr data-testid="user-row" data-email="${esc(u.email)}">
      <td>
        <div style="font-weight:500;">${esc(u.display_name ?? u.email)}</div>
        <div class="muted" style="font-size:11px;">${esc(u.email)}</div>
      </td>
      <td>${roleChip}</td>
      <td data-testid="user-scope-cell">${renderScopeCell(u.bu_scope)}</td>
      <td class="muted">${esc(fmtRelative(u.last_login_at))}</td>
      <td class="muted" style="font-size:11px;">${esc(u.granted_by)}<br>${esc(fmtRelative(u.granted_at))}</td>
      <td>
        <button class="action-link" data-action="toggle-role" data-email="${esc(u.email)}" data-current-role="${u.role}" ${toggleDisabled}>${toggleLabel}</button>
        &nbsp;·&nbsp;
        <button class="action-link" data-action="set-scope" data-email="${esc(u.email)}">Set scope</button>
        &nbsp;·&nbsp;
        <button class="action-link" data-action="revoke" data-email="${esc(u.email)}" ${removeDisabled} style="color:var(--coral);">Revoke</button>
        ${isSelf ? '<span class="muted" style="font-size:11px;margin-left:6px;">(you)</span>' : ""}
      </td>
    </tr>`;
}

function renderRequestRow(req: AccessRequest): string {
  return `
    <tr data-testid="request-row" data-request-id="${req.id}">
      <td>
        <div style="font-weight:500;">${esc(req.display_name ?? req.email)}</div>
        <div class="muted" style="font-size:11px;">${esc(req.email)}</div>
      </td>
      <td><span class="chip">${esc(req.app_key)}</span></td>
      <td class="muted" style="max-width:280px;">${esc(req.message ?? "")}</td>
      <td class="muted" style="font-size:11px;">${esc(fmtRelative(req.created_at))}</td>
      <td>
        <button class="action-link" data-action="approve" data-request-id="${req.id}">Approve</button>
        &nbsp;·&nbsp;
        <button class="action-link" data-action="deny" data-request-id="${req.id}" style="color:var(--coral);">Deny</button>
      </td>
    </tr>`;
}

export async function renderAdminUsersPanel(host: HTMLElement, currentUserEmail: string): Promise<void> {
  host.innerHTML = `<div class="loading">Loading users…</div>`;
  let users: SignalUser[] = [];
  let requests: AccessRequest[] = [];
  try {
    [users, requests] = await Promise.all([listUsers(), listRequests()]);
  } catch (e) {
    host.innerHTML = `<div class="error">Failed to load users: ${esc((e as Error).message)}</div>`;
    return;
  }

  const requestsBlock = requests.length
    ? `
    <h3 style="margin:18px 0 8px;font-size:14px;">
      <span aria-hidden="true">📨</span> Pending access requests
      <span class="row-count">${requests.length}</span>
    </h3>
    <div class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th>User</th>
            <th>App</th>
            <th>Message</th>
            <th>Requested</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody data-testid="requests-tbody">
          ${requests.map(renderRequestRow).join("")}
        </tbody>
      </table>
    </div>`
    : "";

  host.innerHTML = `
    <p class="muted" style="margin:0 0 12px;">
      Platform-level access control for Signal. Grant or revoke access here without
      a redeploy. Self-demotion and self-revocation are blocked.
    </p>

    <div class="filters" data-testid="admin-add-user-form" style="margin-bottom:14px;">
      <input type="email" placeholder="user@bounteous.com" data-testid="add-email-input"
             style="flex:1;min-width:260px;" />
      <select data-testid="add-role-select">
        <option value="viewer" selected>Viewer</option>
        <option value="admin">Admin</option>
      </select>
      <button class="action-link" data-action="add-user" data-testid="add-user-btn"
              style="padding:6px 14px;border:1px solid var(--purple-rain);border-radius:6px;">
        + Grant access
      </button>
    </div>

    ${requestsBlock}

    <h3 style="margin:18px 0 8px;font-size:14px;">
      Signal users
      <span class="row-count">${users.length}</span>
    </h3>
    <div class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Role</th>
            <th>BU scope</th>
            <th>Last login</th>
            <th>Granted</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody data-testid="users-tbody">
          ${users.map((u) => renderUserRow(u, currentUserEmail)).join("")}
        </tbody>
      </table>
    </div>`;

  // ── Add user ───────────────────────────────────────────────────────────
  const addBtn = host.querySelector<HTMLButtonElement>('[data-testid="add-user-btn"]');
  const emailInput = host.querySelector<HTMLInputElement>('[data-testid="add-email-input"]');
  const roleSelect = host.querySelector<HTMLSelectElement>('[data-testid="add-role-select"]');
  addBtn?.addEventListener("click", async () => {
    const email = (emailInput?.value ?? "").trim();
    const role = (roleSelect?.value ?? "viewer") as "viewer" | "admin";
    if (!email) {
      showError(host, "Email required");
      return;
    }
    addBtn.disabled = true;
    addBtn.textContent = "Granting…";
    try {
      await addUser(email, role);
      await renderAdminUsersPanel(host, currentUserEmail);
    } catch (e) {
      showError(host, (e as Error).message);
      addBtn.disabled = false;
      addBtn.textContent = "+ Grant access";
    }
  });

  // ── Delegated handlers for row actions ────────────────────────────────
  // PR-Agent (imp 8): renderAdminUsersPanel is re-invoked after every
  // mutation. Re-binding the click listener every time stacks N copies
  // and triggers N parallel API calls per click. Bind once per host
  // element and stash the latest currentUserEmail + users array on the
  // element so the captured-once handler picks up the freshest values
  // on subsequent renders.
  host.dataset.currentUserEmail = currentUserEmail;
  const boundHost = host as HTMLElement & {
    __adminUsersBound?: boolean;
    __adminUsers?: SignalUser[];
    __adminMutating?: boolean;
  };
  boundHost.__adminUsers = users;
  if (!boundHost.__adminUsersBound) {
    boundHost.__adminUsersBound = true;
    host.addEventListener("click", async (ev) => {
      const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>(
        "button[data-action]"
      );
      if (!btn || btn.disabled) return;
      // SGNL-182 Q-05: re-entrancy guard. A double-click before the
      // first mutation's re-render completes used to read a freshly-
      // assigned `__adminUsers` against the stale DOM, hitting the
      // user-not-found error path. Block subsequent clicks until the
      // in-flight mutation + re-render finishes.
      if (boundHost.__adminMutating) return;
      boundHost.__adminMutating = true;
      const action = btn.dataset.action;
      const currentEmail = host.dataset.currentUserEmail ?? "";
      const latestUsers = boundHost.__adminUsers ?? [];
      try {
        if (action === "toggle-role") {
          const email = btn.dataset.email!;
          const next = btn.dataset.currentRole === "admin" ? "viewer" : "admin";
          await setRole(email, next);
          await renderAdminUsersPanel(host, currentEmail);
        } else if (action === "set-scope") {
          const email = btn.dataset.email!;
          const target = latestUsers.find((u) => u.email === email);
          if (!target) {
            showError(host, "User not found in current list — refreshing");
            await renderAdminUsersPanel(host, currentEmail);
            return;
          }
          await openScopeModal(host, target, async () => {
            await renderAdminUsersPanel(host, currentEmail);
          });
        } else if (action === "revoke") {
          const email = btn.dataset.email!;
          if (!confirm(`Revoke Signal access for ${email}?`)) return;
          await revoke(email);
          await renderAdminUsersPanel(host, currentEmail);
        } else if (action === "approve" || action === "deny") {
          const id = Number(btn.dataset.requestId);
          await reviewRequest(id, action);
          await renderAdminUsersPanel(host, currentEmail);
        }
      } catch (e) {
        showError(host, (e as Error).message);
      } finally {
        boundHost.__adminMutating = false;
      }
    });
  }
}
